import React from 'react';
import styled, { css } from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { GreyBolt } from 'src/assets/grey-bolt';
import { LockIconSimple } from 'src/assets/lock-icon-simple';
import { TapIcon } from 'src/assets/tap-icon';
import { Typography } from 'src/typography';

import type { EducationSectionProps } from '../education-section-props';
import { DesktopEducationSection } from '../desktop-education-section';

export function Benefits({ variant = 'mobile' }: EducationSectionProps): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = variant === 'desktop';

  const benefits = [
    {
      icon: <TapIcon color='currentColor' width={24} height={24} />,
      title: t('dutchiePay.signup.benefits.oneClick.title', '1-click checkouts'),
      description: t(
        'dutchiePay.signup.benefits.oneClick.description',
        'Link your bank once. After that, every checkout is a single tap.'
      ),
    },
    {
      icon: <GreyBolt color='currentColor' width={24} height={24} />,
      title: t('dutchiePay.signup.benefits.express.title', 'Express orders'),
      description: t(
        'dutchiePay.signup.benefits.express.description',
        'Order and pay ahead — prepaid orders are processed faster.'
      ),
    },
    {
      icon: <LockIconSimple color='currentColor' width={20} height={20} />,
      title: t('dutchiePay.signup.benefits.confidence.title', 'Shop with confidence'),
      description: t(
        'dutchiePay.signup.benefits.confidence.description',
        'Your banking credentials are never shared with the retailer.'
      ),
    },
  ];

  const content = (
    <List aria-label={t('dutchiePay.signup.benefits.label', 'Pay by Bank benefits')} $variant={variant}>
      {benefits.map((benefit) => (
        <Item key={benefit.title} $variant={variant}>
          <IconBadge aria-hidden='true' $variant={variant}>
            {benefit.icon}
          </IconBadge>
          <TextContainer>
            <ItemTitle tag='span' size={isDesktop ? 'large' : 'small'}>
              {benefit.title}
            </ItemTitle>
            <ItemDescription tag='span' size={isDesktop ? 'large' : 'small'}>
              {benefit.description}
            </ItemDescription>
          </TextContainer>
        </Item>
      ))}
    </List>
  );

  if (isDesktop) {
    return (
      <DesktopEducationSection
        eyebrow={t('dutchiePay.signup.benefits.eyebrow', 'Benefits')}
        title={t('dutchiePay.signup.benefits.desktopTitle', 'Why pay by bank?')}
        titleId='pbb-benefits-title'
      >
        {content}
      </DesktopEducationSection>
    );
  }

  return content;
}

const List = styled.ul<{ $variant: 'desktop' | 'mobile' }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 8px;

  ${({ $variant }) =>
    $variant === 'desktop' &&
    css`
      flex-direction: column;
      gap: 20px;
    `}
`;

const Item = styled.li<{ $variant: 'desktop' | 'mobile' }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;

  ${({ $variant }) =>
    $variant === 'desktop' &&
    css`
      flex-direction: row;
      text-align: left;
      gap: 16px;
      align-items: flex-start;
    `}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const IconBadge = styled.span<{ $variant: 'desktop' | 'mobile' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 4px;
  border-radius: var(--dcm-pbb-benefits-icon-badge-border-radius, 50%);
  background-color: var(--dcm-pbb-benefits-icon-badge-background, ${({ theme }) => theme.colors.green10});
  color: var(--dcm-pbb-benefits-icon-color, ${({ theme }) => theme.colors.dutchiePayGreen});

  ${({ $variant }) =>
    $variant === 'desktop' &&
    css`
      background-color: var(--dcm-pbb-benefits-icon-badge-background, ${({ theme }) => theme.colors.green20});
      flex-shrink: 0;
      margin-bottom: 0;
    `}
`;

const ItemTitle = styled(Typography.Title)`
  color: var(--dcm-pbb-benefits-title-color, ${({ theme }) => theme.colors.dutchiePayBlack});
`;

const ItemDescription = styled(Typography.Body)`
  color: var(--dcm-pbb-benefits-description-color, ${({ theme }) => theme.colors.grey[35]});
`;
